// RetornoSuelta.jsx
//
// #1887 · la vuelta de Flow tras pagar jornadas sueltas (spec `.claude/specs/jornada-suelta/`, T29, R36 y R74). El backend devuelve al médico
// a `<FRONTEND_BASE_URL>/perfil?pago=suelta` (R19) tras mostrarle su propia página de resultado sin datos de la cuenta. Al montar la
// app con ese parámetro se abre este diálogo: pide `GET /me/suelta/` y dice lo que dice la ÚLTIMA compra (acreditada, en proceso o no
// confirmada), sondeando cada 3 s hasta 60 s mientras siga `pendiente` (el webhook de Flow suele llegar antes de que vuelva, pero no
// siempre). Nada se acredita desde acá: la verdad es el estado que el backend guarda tras `payment/getStatus`.
// `?pago=flow` (la suscripción) NO se toca: lo sigue abriendo `RetornoPago`. El parámetro se limpia de la URL para que recargar
// no reabra el diálogo. Con la compra acreditada muestra cuántas jornadas quedaron guardadas, de qué tipo y hasta cuándo (R74) y la
// banda que verá cada vez que entre (la banda real: si no hay nada que activar, no se dibuja).
// Mockup: `jornada_suelta_pago.pdf`, pantalla 5.
// Cubre R36, R74 — .claude/specs/jornada-suelta/
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogContent, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SOPORTE } from "../../helpers/soporte";
import { diaMes, nombreTipoPlural, pedirOferta } from "../../helpers/suelta-api";
import { recargarJornada } from "../../hooks/useJornadaEstado";
import JornadaBanda from "../jornada/JornadaBanda";
import { Z_ENCIMA_DEL_GATE } from "../jornada/estilos";
import { botonGhostSx, botonPrimarioSx } from "../checkout/botonesSx";

const INTERVALO_MS = 3000;
const PLAZO_MS = 60000;

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

const Resultado = ({ testId, icono, titulo, lead, children }) => (
  <Box data-testid={testId} sx={{ textAlign: "center", py: 1 }}>
    <Box sx={{ display: "grid", placeItems: "center", mb: 1.75 }}>{icono}</Box>
    <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25, color: "var(--text-primary)" }}>{titulo}</Typography>
    {lead ? <Typography sx={{ fontSize: 13.5, color: "var(--text-secondary)", mt: 1, lineHeight: 1.5 }}>{lead}</Typography> : null}
    {children}
  </Box>
);

const Retorno = ({ onCerrar }) => {
  const navigate = useNavigate();
  const [fase, setFase] = useState("cargando"); // cargando | pendiente | acreditada | no_confirmada
  const [ultima, setUltima] = useState(null);
  const [agotado, setAgotado] = useState(false);
  const [vuelta, setVuelta] = useState(0);

  useEffect(() => {
    let cancelado = false;
    const inicio = Date.now();
    (async () => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let u = null;
        let ok = false;
        try {
          const o = await pedirOferta();
          u = o?.ultima || null;
          ok = true;
        } catch {
          /* red caída un momento: se sigue intentando hasta cumplir el plazo */
        }
        if (cancelado) return;
        if (ok) {
          setUltima(u);
          if (u?.estado === "acreditada") {
            setFase("acreditada");
            recargarJornada();
            return;
          }
          if (u?.estado === "pendiente" || !u) {
            setFase(u ? "pendiente" : "no_confirmada");
            if (!u) return;
          } else {
            // expirada, fallida, rechazada, anulada, conflicto: estados finales sin saldo.
            setFase("no_confirmada");
            return;
          }
        } else {
          setFase((f) => (f === "cargando" ? "pendiente" : f));
        }
        if (Date.now() - inicio + INTERVALO_MS > PLAZO_MS) {
          setAgotado(true);
          return;
        }
        await esperar(INTERVALO_MS);
        if (cancelado) return;
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [vuelta]);

  if (fase === "acreditada") {
    const n = ultima?.cantidad || 1;
    const nombre = nombreTipoPlural(ultima?.tipo, n).replace(/^\d+ /, "");
    return (
      <Resultado
        testId="suelta-resultado-acreditada"
        icono={
          <Box sx={{ width: 58, height: 58, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: "rgba(var(--color-success-rgb), 0.16)" }}>
            <CheckIcon sx={{ fontSize: 30, color: "var(--color-success)" }} />
          </Box>
        }
        titulo={
          <>
            {"Tienes "}
            <Box component="span" data-testid="suelta-resultado-cantidad" sx={{ color: "var(--color-primary-soft)" }}>{`${n} ${nombre}`}</Box>
            {n === 1 ? " guardada" : " guardadas"}
          </>
        }
        lead={
          <>
            {"Duran hasta el "}
            <Box component="span" data-testid="suelta-resultado-vence" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{diaMes(ultima?.vence_en)}</Box>
            {" (60 días). Las activas tú con el botón."}
          </>
        }
      >
        <Box sx={{ textAlign: "left", mt: 2.25 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-faint)", textTransform: "uppercase", mb: 0.75 }}>
            Así lo verás cada vez que entres
          </Typography>
          {/* `mobile`: el diálogo mide ~440 px como mucho; la banda de escritorio (en fila) no cabe ahí y se desarmaba. */}
          <JornadaBanda mobile />
        </Box>
        <Button
          data-testid="suelta-ir-a-pacientes"
          variant="contained"
          disableElevation
          fullWidth
          onClick={() => {
            onCerrar();
            navigate("/turno");
          }}
          sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14, mt: 2.5 }}
        >
          Ir a mis pacientes
        </Button>
      </Resultado>
    );
  }

  if (fase === "no_confirmada") {
    return (
      <Resultado
        testId="suelta-resultado-no-confirmada"
        icono={<ErrorOutlineIcon sx={{ fontSize: 56, color: "var(--color-warning)" }} />}
        titulo="No pudimos confirmarlo con Flow"
        lead={`Si Flow te cobró, escríbenos a ${SOPORTE} y lo resolvemos hoy. Si no, puedes volver a intentarlo cuando quieras.`}
      >
        <Button variant="outlined" onClick={onCerrar} sx={{ ...botonGhostSx, mt: 2 }}>Cerrar</Button>
      </Resultado>
    );
  }

  // cargando | pendiente
  return (
    <Resultado
      testId="suelta-resultado-pendiente"
      icono={<AutorenewIcon sx={{ fontSize: 56, color: "var(--color-warning)" }} />}
      titulo="Estamos confirmando con Flow"
      lead="Esto toma unos segundos. No se cobra dos veces."
    >
      {agotado ? (
        <>
          <Typography sx={{ fontSize: 12, color: "var(--text-faint)", mt: 1.5, lineHeight: 1.5 }}>
            {`Todavía no llega la confirmación de Flow. Si ya pagaste, dale unos minutos. Si pasa más de una hora, escríbenos a ${SOPORTE}.`}
          </Typography>
          <Button
            data-testid="suelta-revisar"
            variant="outlined"
            onClick={() => {
              setAgotado(false);
              setVuelta((v) => v + 1);
            }}
            sx={{ ...botonGhostSx, mt: 1.5 }}
          >
            Volver a revisar
          </Button>
        </>
      ) : null}
    </Resultado>
  );
};

const RetornoSuelta = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const theme = useTheme();
  const pantallaCompleta = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("pago") !== "suelta") return;
    setAbierto(true);
    params.delete("pago");
    const resto = params.toString();
    navigate({ pathname: location.pathname, search: resto ? `?${resto}` : "", hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);

  if (!abierto) return null;
  return (
    <Dialog
      open
      onClose={() => setAbierto(false)}
      fullScreen={pantallaCompleta}
      maxWidth="xs"
      fullWidth
      aria-label="Resultado del pago de tus jornadas adicionales"
      sx={{ zIndex: Z_ENCIMA_DEL_GATE }}
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-elevated)",
          backgroundImage: "none",
          border: pantallaCompleta ? 0 : "1px solid var(--border-soft)",
          borderRadius: pantallaCompleta ? 0 : "16px",
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Retorno onCerrar={() => setAbierto(false)} />
      </DialogContent>
    </Dialog>
  );
};

export default RetornoSuelta;
